/**
 * Guest store — PERSISTS to localStorage (iter 50).
 *
 * NOTE: An earlier V3 spec commented "in-memory only, not localStorage".
 * That was a product decision, NOT a privacy constraint — verified in
 * iter50. Reversed here deliberately because the acquisition-funnel
 * relies on scans surviving a page reload. If a future iteration needs
 * to remove persistence again, keep this comment as the paper trail.
 *
 * What we persist (per-origin, per-browser):
 *   anon_id             — UUID v4, first seen
 *   scanCount           — count of AI + Manual + Barcode logs
 *   pendingFoodScans    — array of scan payloads (up to 5); each carries
 *                         dish name, POST-multiplier macros, ISO
 *                         scanned_at with offset, 200x200 thumbnail
 *                         base64, client_scan_id.
 *   lastSoftGateContext — remembered when 429 fires so we can adapt copy
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export const GUEST_KEY = "fitfrik_guest_mode";
const STORE_KEY = "fitfrik_guest_store_v1";

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function isGuest() {
  return typeof sessionStorage !== "undefined" &&
    sessionStorage.getItem(GUEST_KEY) === "true";
}
export function setGuestMode(on) {
  if (typeof sessionStorage === "undefined") return;
  if (on) sessionStorage.setItem(GUEST_KEY, "true");
  else sessionStorage.removeItem(GUEST_KEY);
}

export const useGuestStore = create(
  persist(
    (set, get) => ({
      anon_id: _uuid(),
      pendingFoodScans: [],
      scanCount: 0,
      lastSoftGateContext: null,

      ensureAnonId: () => {
        if (!get().anon_id) set({ anon_id: _uuid() });
        return get().anon_id;
      },

      addScan: (scan) => set((s) => {
        // Cap at 5 to protect the 5-10 MB localStorage quota.
        const next = [...s.pendingFoodScans, scan].slice(-5);
        return { pendingFoodScans: next, scanCount: s.scanCount + 1 };
      }),

      // Session 4B — guest-saved products (canonical records, no images).
      // Migrated into saved_products on signup via /guest/migrate.
      savedProducts: [],
      addSavedProduct: (canonical) => set((s) => {
        const p = canonical?.product || {};
        const dupe = s.savedProducts.find((r) =>
          (r.canonical?.product?.name || null) === (p.name || null) &&
          (r.canonical?.product?.brand || null) === (p.brand || null));
        if (dupe) return {};
        const row = {
          product_id: `local_${Date.now()}`,
          canonical,
          saved_at: new Date().toISOString(),
        };
        return { savedProducts: [row, ...s.savedProducts].slice(0, 10) };
      }),
      removeSavedProduct: (pid) => set((s) => ({
        savedProducts: s.savedProducts.filter((r) => r.product_id !== pid),
      })),

      // Iter 51: sync multiplier changes / final macro edits back to the
      // pending scan record so the 6th-scan summary shows POST-multiplier
      // totals and /guest/migrate persists the doubled values.
      updateScanByClientId: (client_scan_id, patch) => set((s) => ({
        pendingFoodScans: s.pendingFoodScans.map((scan) =>
          scan.client_scan_id === client_scan_id ? { ...scan, ...patch } : scan
        ),
      })),

      // Iter 51: after guest hits Save in results, drop any items the user
      // unticked. `keepIds` is the set of client_scan_ids the user chose to
      // keep from the most-recent scan batch.
      pruneScansExcept: (keepIds) => set((s) => ({
        pendingFoodScans: s.pendingFoodScans.filter((scan) =>
          !scan.client_scan_id || keepIds.includes(scan.client_scan_id)
        ),
      })),

      clear: () => set({
        pendingFoodScans: [],
        scanCount: 0,
        lastSoftGateContext: null,
        // Deliberately DO NOT clear anon_id here — we want a returning
        // guest to be recognised so scan counts don't reset by accident.
      }),

      resetIdentity: () => set({ anon_id: _uuid() }),

      setLastSoftGateContext: (ctx) => set({ lastSoftGateContext: ctx }),
    }),
    {
      name: STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        anon_id: s.anon_id,
        pendingFoodScans: s.pendingFoodScans,
        scanCount: s.scanCount,
        savedProducts: s.savedProducts,
      }),
    }
  )
);

// Testability
if (typeof window !== "undefined") {
  window.__ff_guestStore = useGuestStore;
  window.__ff_setGuestMode = setGuestMode;
}

// Lightweight tracker — sends to own backend, PostHog if present.
export async function trackGuest(event, meta = {}) {
  const st = useGuestStore.getState();
  try {
    const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api/guest/track";
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ anon_id: st.anon_id, event, meta, scan_index: st.scanCount }),
      keepalive: true,
    });
  } catch { /* non-fatal */ }
  try {
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture(`guest_${event}`, { anon_id: st.anon_id, ...meta });
    }
  } catch { /* non-fatal */ }
}
